/**
 * How a `PeriodSelection` travels: parsed out of the query string on the way
 * in, written back as a param patch on the way out, and carried onto links
 * that point at a page the period filters.
 */

import {
  currentFiscalYear,
  currentFiscalYearSelection,
  isValidIsoDate,
  normalizeFiscalYearEndMonth,
  type PeriodSelection,
} from './fiscal-period';

/** Query-string keys the selection lives in — carried across pages. */
export const PERIOD_PARAM_KEYS = ['period', 'from', 'to'] as const;

/** Routes whose content the period filters; links elsewhere drop the params. */
export const PERIOD_AWARE_ROUTES = [
  '/quotes',
  '/bills',
  '/expenses',
  '/accounting/bank',
] as const;

interface PeriodParamsLike {
  get(key: string): string | null;
}

/** Anything unparseable falls back to the current fiscal year. */
export function parsePeriodSelection(
  params: PeriodParamsLike,
  endMonth: number,
  today: Date = new Date(),
): PeriodSelection {
  const month = normalizeFiscalYearEndMonth(endMonth);
  const preset = params.get('period');
  if (preset) {
    const fy = /^fy-(\d{4})$/.exec(preset);
    if (fy) return { kind: 'fy', endYear: Number(fy[1]) };
    const semester = /^s([12])-(\d{4})$/.exec(preset);
    if (semester) {
      return {
        kind: 'semester',
        endYear: Number(semester[2]),
        index: Number(semester[1]) as 1 | 2,
      };
    }
    const quarter = /^q([1-4])-(\d{4})$/.exec(preset);
    if (quarter) {
      return {
        kind: 'quarter',
        endYear: Number(quarter[2]),
        index: Number(quarter[1]) as 1 | 2 | 3 | 4,
      };
    }
    const monthMatch = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(preset);
    if (monthMatch) {
      return {
        kind: 'month',
        year: Number(monthMatch[1]),
        month: Number(monthMatch[2]),
      };
    }
    return currentFiscalYearSelection(month, today);
  }

  const from = params.get('from');
  const to = params.get('to');
  if (from && to && isValidIsoDate(from) && isValidIsoDate(to) && from <= to) {
    return { kind: 'custom', from, to };
  }
  return currentFiscalYearSelection(month, today);
}

/**
 * Param patch for a selection — `null` deletes the key, so the default
 * (current fiscal year) stays out of the URL entirely.
 */
export function serializePeriodSelection(
  selection: PeriodSelection,
  endMonth: number,
  today: Date = new Date(),
): Record<(typeof PERIOD_PARAM_KEYS)[number], string | null> {
  const empty = { period: null, from: null, to: null };
  switch (selection.kind) {
    case 'fy':
      return selection.endYear === currentFiscalYear(endMonth, today)
        ? empty
        : { ...empty, period: `fy-${selection.endYear}` };
    case 'semester':
      return { ...empty, period: `s${selection.index}-${selection.endYear}` };
    case 'quarter':
      return { ...empty, period: `q${selection.index}-${selection.endYear}` };
    case 'month':
      return {
        ...empty,
        period: `${selection.year}-${String(selection.month).padStart(2, '0')}`,
      };
    case 'custom':
      return { period: null, from: selection.from, to: selection.to };
  }
}

function isPeriodAwareRoute(pathname: string): boolean {
  return PERIOD_AWARE_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

/**
 * Carries the current period params onto a link, but only when it points at a
 * page the period actually filters. Params already spelled out in `to` win.
 */
export function withPeriodParams(to: string, currentSearch: string): string {
  const [pathname, ownSearch = ''] = to.split('?');
  if (!isPeriodAwareRoute(pathname)) return to;
  const current = new URLSearchParams(currentSearch);
  const next = new URLSearchParams(ownSearch);
  for (const key of PERIOD_PARAM_KEYS) {
    const value = current.get(key);
    if (value !== null && !next.has(key)) next.set(key, value);
  }
  const search = next.toString();
  return search ? `${pathname}?${search}` : pathname;
}
